import React, { useState } from 'react';
import { Upload, Input } from 'antd';
import { appMessage as message } from '../../utils/appMessage';
import type { UploadFile, UploadProps } from 'antd';
import { InboxOutlined } from '@ant-design/icons';
import { AppModal } from '../common/AppModal';
import { AppButton } from '../common/AppButton';
import { submitRegistrationRequest } from '../../api/api';
import { RegistrableGameType, GAME_TYPE_LABEL } from '../../types/productRegistration';
import { SPACING, FONT_SIZE } from '../../constants/constants';
import { useThemeTokens } from '../../hooks/UseThemeTokens';

const { TextArea } = Input;

interface Props {
  open:     boolean;
  gameType: RegistrableGameType;
  onClose:  () => void;
}

/**
 * "검색해도 안 나오는 카드"(공식 홈페이지에 아직 없거나 PriceHub가 놓친 카드)를 사진만
 * 올려서 제보하는 모달(2026-08-25, 사용자 요청). 카드 정보를 몰라도 되고 사진 한 장이면
 * 끝 — pricehub 관리자가 "카드 등록 요청" 도구에서 확인 후 카탈로그에 직접 추가한다.
 */
export const RegistrationRequestModal: React.FC<Props> = ({ open, gameType, onClose }) => {
  const t = useThemeTokens();
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleChange: UploadProps['onChange'] = ({ fileList: next }) => {
    // 한 장만 — 새로 고르면 이전 것은 자동 교체.
    setFileList(next.slice(-1));
  };

  const handleSubmit = async () => {
    const file = fileList[0]?.originFileObj;
    if (!file) {
      message.warning('사진을 먼저 선택하세요');
      return;
    }
    setSubmitting(true);
    try {
      await submitRegistrationRequest(gameType, file, note.trim() || undefined);
      message.success('등록 요청을 보냈습니다 — pricehub 관리자가 확인 후 등록합니다');
      setFileList([]);
      setNote('');
      onClose();
    } catch {
      message.error('등록 요청 전송에 실패했습니다');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AppModal
      title={`카드 등록 요청 — ${GAME_TYPE_LABEL[gameType]}`}
      open={open}
      onCancel={onClose}
      footer={[
        <AppButton key="cancel" variant="text" onClick={onClose}>취소</AppButton>,
        <AppButton key="submit" variant="primary" loading={submitting} onClick={handleSubmit}>
          등록 요청 보내기
        </AppButton>,
      ]}
    >
      <p style={{ fontSize: FONT_SIZE.sm, color: t.textSecondary, marginTop: 0, marginBottom: SPACING.lg }}>
        검색해도 안 나오는 카드(공식 홈페이지에 아직 없는 신규 카드 등)를 사진으로 찍어
        올리면 pricehub 관리자가 확인 후 카탈로그에 등록합니다. 카드 정보를 직접 입력할
        필요는 없습니다 — 사진만 있으면 됩니다.
      </p>

      <Upload.Dragger
        accept="image/*"
        fileList={fileList}
        onChange={handleChange}
        beforeUpload={() => false}
        maxCount={1}
        listType="picture"
      >
        <p className="ant-upload-drag-icon"><InboxOutlined /></p>
        <p className="ant-upload-text">카드 사진을 클릭하거나 끌어다 놓으세요</p>
      </Upload.Dragger>

      <TextArea
        placeholder="메모(선택) — 예: 신규 확장팩 카드, 레어도 확인 안 됨"
        value={note}
        onChange={(e) => setNote(e.target.value)}
        rows={2}
        style={{ marginTop: SPACING.md }}
      />
    </AppModal>
  );
};
